import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtAUD, getToken } from "../lib/api";
import { Search, FileText, Trash2, Download, Plus, Mail } from "lucide-react";

const PRODUCT_LABELS = {
  straight_ramp: "Straight Rubber Ramp",
  wing_ramp: "Rubber Ramp w/ Wings",
  infill: "Custom Rubber Infill",
  step: "Rubber Step / Platform",
};

export default function QuoteHistory() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotes", { params: q ? { q } : {} });
      setItems(data || []);
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    await api.delete(`/quotes/${id}`);
    setItems(s => s.filter(x => x.id !== id));
  };

  const downloadPdf = async (id, qn) => {
    const url = `${api.defaults.baseURL}/quotes/${id}/pdf`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${qn}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <div className="space-y-5" data-testid="quotes-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#fda500] font-bold mb-1">Quote Archive</div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0A1128]">Quote History</h1>
        </div>
        <Link to="/quote-portal/products" className="bg-[#fda500] hover:bg-[#e59500] text-[#0A1128] font-bold px-4 py-2 rounded-md flex items-center gap-2 transition shadow-md" data-testid="new-quote-btn">
          <Plus size={14} /> New Quote
        </Link>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2.5 bg-slate-50/60">
          <Search size={15} className="text-slate-400" />
          <input
            data-testid="quotes-search"
            value={q}
            onChange={(e)=>setQ(e.target.value)}
            placeholder="Search by customer or quote number…"
            className="bg-transparent outline-none w-full text-sm placeholder-slate-400"
          />
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-400">No quotes found</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-600 border-b border-slate-200">
                <th className="px-4 py-3 font-semibold">Quote Number</th>
                <th className="px-4 py-3 font-semibold">Customer Name</th>
                <th className="px-4 py-3 font-semibold">Product Type</th>
                <th className="px-4 py-3 font-semibold">Selected Scope</th>
                <th className="px-4 py-3 font-semibold">Created By</th>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 text-right font-semibold">Final Total</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((qq, idx) => {
                const sel = qq.selected_scope || "wholesale";
                const total = qq.calculation?.scopes?.[sel]?.sale_price || 0;
                const pt = qq.product_type || "—";
                const ptLabel = PRODUCT_LABELS[pt] || pt;
                return (
                  <tr key={qq.id} className={`border-b border-slate-100 hover:bg-slate-50/70 transition ${idx % 2 === 1 ? "bg-slate-50/30" : ""}`} data-testid={`quote-row-${qq.id}`}>
                    <td className="px-4 py-3.5 font-mono text-xs">
                      <Link className="text-[#254391] hover:text-[#fda500] font-semibold" to={`/quote-portal/quotes/${qq.id}`}>{qq.quote_number}</Link>
                    </td>
                    <td className="px-4 py-3.5 font-medium text-[#0A1128]">{qq.customer_name}</td>
                    <td className="px-4 py-3.5 text-xs text-slate-700">{ptLabel}</td>
                    <td className="px-4 py-3.5">
                      <span className="inline-block bg-[#fff8e7] border border-[#fda500]/40 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded">
                        {sel.replace("_"," ")}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-xs text-slate-600">{qq.created_by?.name}</td>
                    <td className="px-4 py-3.5 font-mono text-xs text-slate-500">{(qq.created_at || "").slice(0,16).replace("T"," ")}</td>
                    <td className="px-4 py-3.5 text-right font-mono font-semibold text-[#0A1128]">{fmtAUD(total)}</td>
                    <td className="px-4 py-3.5 text-right">
                      <div className="inline-flex gap-1">
                        <Link to={`/quote-portal/quotes/${qq.id}`} className="p-1.5 rounded text-[#254391] hover:bg-blue-50 inline-block" title="View quote" data-testid={`view-${qq.id}`}>
                          <FileText size={14} />
                        </Link>
                        <button onClick={() => downloadPdf(qq.id, qq.quote_number)} className="p-1.5 rounded text-[#254391] hover:bg-blue-50" data-testid={`pdf-${qq.id}`} title="Download PDF">
                          <Download size={14} />
                        </button>
                        <Link to={`/quote-portal/quotes/${qq.id}?action=email`} className="p-1.5 rounded text-[#254391] hover:bg-blue-50 inline-block" title="Email quote" data-testid={`email-${qq.id}`}>
                          <Mail size={14} />
                        </Link>
                        <button onClick={() => del(qq.id)} className="p-1.5 rounded text-red-500 hover:bg-red-50" data-testid={`del-${qq.id}`} title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
